import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import DashboardFilters from "@/components/DashboardFilters";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircle2,
  Clock,
  FileText,
  AlertTriangle,
  TrendingUp,
  Users,
  Layers,
  IndianRupee,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Receipt,
  ClipboardList,
  FileQuestion,
  ArrowRight,
  Calendar,
  Globe,
  FolderOpen,
  Target,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
];

const STATUS_COLORS = {
  generated: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  sent: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  paid: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  unpaid: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  overdue: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
};

const formatINR = (value) => {
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value?.toFixed(0);
};

const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
  <div className={`stat-card animate-fadeIn ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`} data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`} onClick={onClick}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="stat-label mb-1">{title}</p>
        <p className="stat-value">{value}</p>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className={`stat-icon ${color} flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label, dark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={`rounded-lg p-3 shadow-lg border ${dark ? "bg-[#1a1d27] border-[#2a2d3a] text-slate-200" : "bg-white border-slate-200"}`}>
      <p className={`text-sm font-medium ${dark ? "text-slate-200" : "text-slate-900"}`}>{label || payload[0]?.name}</p>
      {payload.map((entry, i) => (
        <p key={i} className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>
          {entry.name}: <span className="font-semibold" style={{ color: entry.color }}>
            {typeof entry.value === 'number' && entry.value > 100
              ? `₹${entry.value.toLocaleString('en-IN')}`
              : entry.value}
          </span>
        </p>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [enhanced, setEnhanced] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [filtered, setFiltered] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [claimStats, setClaimStats] = useState({ pending: 0, approved: 0, rejected: 0, under_review: 0 });
  const [assignmentStats, setAssignmentStats] = useState({ total: 0, total_amount: 0, ready: 0, invoiced: 0 });
  const [pendencies, setPendencies] = useState({ pendencies: [], total_blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [filterParams, setFilterParams] = useState({});
  const navigate = useNavigate();
  const { dark } = useTheme();
  const { user } = useAuth();

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [basicRes, enhancedRes, statusRes, claimsRes, assignmentsRes, pendRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/enhanced-stats`),
        axios.get(`${API}/system-status`),
        axios.get(`${API}/claims/`).catch(() => ({ data: { stats: { pending: 0, approved: 0, rejected: 0, under_review: 0 } } })),
        axios.get(`${API}/assignments`).catch(() => ({ data: { assignments: [] } })),
        axios.get(`${API}/admin/pendencies`).catch(() => ({ data: { pendencies: [], total_blocked: 0 } })),
      ]);
      setStats(basicRes.data);
      setEnhanced(enhancedRes.data);
      setSystemStatus(statusRes.data);
      setClaimStats(claimsRes.data?.stats || { pending: 0, approved: 0, rejected: 0, under_review: 0 });
      const assignments = assignmentsRes.data?.assignments || [];
      setAssignmentStats({
        total: assignments.length,
        total_amount: assignments.reduce((s, a) => s + (a.amount || 0), 0),
        ready: assignments.filter(a => a.invoice_status === "ready").length,
        invoiced: assignments.filter(a => a.invoice_status === "invoiced" || a.invoice_status === "paid").length,
      });
      setPendencies(pendRes.data || { pendencies: [], total_blocked: 0 });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchFiltered = useCallback(async (params) => {
    try {
      const qp = new URLSearchParams(params);
      const res = await axios.get(`${API}/dashboard/filtered?${qp}`);
      setFiltered(res.data);
      setAnalytics(res.data); // Use filtered data for analytics charts
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchFiltered(filterParams); }, [filterParams, fetchFiltered]);

  const handleFiltersChange = useCallback((params) => { setFilterParams(params); }, []);
  const [trendGranularity, setTrendGranularity] = useState("monthly");

  // Use filtered data for charts when available
  const chartData = filtered || {};
  const taskTypeDist = chartData.task_type_distribution || enhanced?.task_type_distribution || [];
  const topResources = chartData.top_resources || enhanced?.top_resources || [];
  const monthlyTrends = chartData.monthly_trends || [];
  const weeklyTrends = chartData.weekly_trends || [];
  const dailyVolume = chartData.daily_volume || [];
  const langBreakdown = chartData.language_breakdown || [];
  const topProjects = chartData.top_projects || [];
  const rateCoverage = chartData.rate_coverage || null;

  // Select trend data based on granularity
  const trendData = (trendGranularity === "daily" || trendGranularity === "today")
    ? dailyVolume.map(d => ({ label: d.date.slice(5), tasks: d.count, amount: 0 }))
    : trendGranularity === "weekly"
    ? weeklyTrends.map(w => ({ label: w.week.slice(5), tasks: w.tasks, amount: w.amount }))
    : monthlyTrends.map(m => ({ label: m.month, tasks: m.tasks, amount: m.amount }));

  const trendLabel = trendGranularity === "daily" ? "Daily" : trendGranularity === "weekly" ? "Weekly" : trendGranularity === "monthly" ? "This Month" : "Monthly";

  // Click-through navigation
  const drillTo = (page, params = {}) => {
    const qp = new URLSearchParams(params).toString();
    navigate(`${page}${qp ? `?${qp}` : ""}`);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const completionPct = stats?.total_tasks
    ? Math.round((stats.completed_tasks / stats.total_tasks) * 100)
    : 0;

  const invoiceStatusData = enhanced?.invoice_status_summary
    ? Object.entries(enhanced.invoice_status_summary)
        .filter(([, v]) => v > 0)
        .map(([key, value]) => ({ name: key.charAt(0).toUpperCase() + key.slice(1), value }))
    : [];

  const verificationData = enhanced?.verification_breakdown
    ? Object.entries(enhanced.verification_breakdown)
        .filter(([, v]) => v > 0)
        .map(([key, value]) => ({ name: key, value }))
    : [];

  const formatLastUpdate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const getLastDataUpdate = () => {
    if (!systemStatus) return null;
    const dates = [
      systemStatus.last_task_upload,
      systemStatus.last_duration_upload,
      systemStatus.last_sync,
      systemStatus.last_merge,
      systemStatus.last_rate_application,
    ].filter(Boolean).map(d => new Date(d));
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates));
  };

  const lastUpdate = getLastDataUpdate();

  return (
    <div className={`min-h-screen p-6 lg:p-8 transition-colors duration-200 ${dark ? "bg-[#0b0e14]" : "bg-gradient-to-br from-slate-50 via-white to-blue-50/30"}`} data-testid="dashboard-page">
      {/* ── Welcome + Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className={`text-xs font-medium ${dark ? "text-blue-400" : "text-blue-600"}`} data-testid="welcome-greeting">{getGreeting()}, {user?.display_name || "Admin"}</p>
          <h1 className={`text-2xl font-bold tracking-tight ${dark ? "text-slate-100" : "text-slate-900"}`} style={{ fontFamily: "Manrope, sans-serif" }}>Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdate && (
            <div className="text-right" data-testid="last-update-info">
              <p className={`text-[9px] uppercase tracking-widest ${dark ? "text-slate-600" : "text-slate-400"}`}>Last Updated</p>
              <p className={`text-xs font-semibold ${dark ? "text-slate-400" : "text-slate-600"}`}>{formatLastUpdate(lastUpdate)}</p>
            </div>
          )}
          <button onClick={fetchStats} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all hover:shadow-lg active:scale-95 ${dark ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-slate-900 text-white hover:bg-slate-800"}`} data-testid="refresh-dashboard-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* ── Sticky Filters ── */}
      <div className={`sticky top-0 z-20 -mx-6 lg:-mx-8 px-6 lg:px-8 py-2.5 backdrop-blur-xl border-b mb-5 ${dark ? "bg-[#0b0e14]/90 border-[#1e2231]" : "bg-white/80 border-slate-200/60"}`} data-testid="sticky-filters">
        <DashboardFilters onFiltersChange={handleFiltersChange} onGranularityChange={setTrendGranularity} dark={dark} />
      </div>

      {/* ── KPI Hero Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <div className={`group relative overflow-hidden rounded-2xl p-6 text-white cursor-pointer hover:shadow-2xl transition-all hover:-translate-y-1 active:scale-[0.98] ${dark ? "bg-gradient-to-br from-slate-700 to-slate-800 hover:shadow-slate-700/20" : "bg-gradient-to-br from-slate-800 to-slate-900 hover:shadow-slate-900/20"}`} onClick={() => navigate("/adjustments")} data-testid="stat-total-tasks">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
          <Layers className="w-8 h-8 mb-4 text-slate-400" />
          <p className="text-3xl font-bold tracking-tight">{(filtered?.total_tasks ?? stats?.total_tasks ?? 0).toLocaleString()}</p>
          <p className="text-sm text-slate-400 mt-1">Total Tasks</p>
        </div>
        <div className={`group relative overflow-hidden rounded-2xl p-6 text-white cursor-pointer hover:shadow-2xl transition-all hover:-translate-y-1 active:scale-[0.98] ${dark ? "bg-gradient-to-br from-emerald-600 to-teal-700 hover:shadow-emerald-600/20" : "bg-gradient-to-br from-emerald-500 to-teal-600 hover:shadow-emerald-500/20"}`} onClick={() => navigate("/adjustments")} data-testid="stat-completed-tasks">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
          <CheckCircle2 className="w-8 h-8 mb-4 text-emerald-200" />
          <p className="text-3xl font-bold tracking-tight">{(filtered?.completed_tasks ?? stats?.completed_tasks ?? 0).toLocaleString()}</p>
          <p className="text-sm text-emerald-100 mt-1">{filtered ? Math.round((filtered.completed_tasks / (filtered.total_tasks || 1)) * 100) : completionPct}% complete</p>
        </div>
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 p-6 text-white cursor-pointer hover:shadow-2xl hover:shadow-violet-500/20 transition-all hover:-translate-y-1 active:scale-[0.98]" onClick={() => navigate("/resource-center")} data-testid="stat-resources">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
          <Users className="w-8 h-8 mb-4 text-violet-200" />
          <p className="text-3xl font-bold tracking-tight">{filtered?.unique_resources ?? stats?.unique_resources?.length ?? 0}</p>
          <p className="text-sm text-violet-100 mt-1">Active Resources</p>
        </div>
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white cursor-pointer hover:shadow-2xl hover:shadow-blue-500/20 transition-all hover:-translate-y-1 active:scale-[0.98]" onClick={() => navigate("/finance")} data-testid="stat-total-amount">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500" />
          <IndianRupee className="w-8 h-8 mb-4 text-blue-200" />
          <p className="text-3xl font-bold tracking-tight">{`₹${((filtered?.total_amount ?? stats?.total_amount ?? 0) / 100000).toFixed(2)}L`}</p>
          <p className="text-sm text-blue-100 mt-1">Total Amount</p>
        </div>
      </div>

      {/* ── Quick Action Widgets ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <Card className={`group border-0 shadow-sm hover:shadow-xl transition-all cursor-pointer rounded-2xl overflow-hidden hover:-translate-y-1 ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`} onClick={() => navigate("/grouping?tab=assignments")} data-testid="assignments-widget">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all ${dark ? "bg-purple-500/15" : "bg-purple-100 group-hover:bg-purple-200"}`}>
                <ClipboardList className={`w-6 h-6 ${dark ? "text-purple-400" : "text-purple-600"}`} />
              </div>
              <ArrowRight className={`w-4 h-4 ${dark ? "text-slate-600" : "text-slate-400"}`} />
            </div>
            <p className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{assignmentStats.total}</p>
            <p className={`text-xs mb-2 ${dark ? "text-slate-500" : "text-slate-500"}`}>Task Assignments</p>
            <div className="flex items-center gap-3 text-[11px]">
              <span className={`font-medium ${dark ? "text-emerald-400" : "text-emerald-600"}`}>₹{assignmentStats.total_amount.toLocaleString('en-IN')}</span>
              <span className={dark ? "text-slate-600" : "text-slate-400"}>•</span>
              <span className={dark ? "text-blue-400" : "text-blue-600"}>{assignmentStats.ready} ready</span>
              <span className={dark ? "text-slate-600" : "text-slate-400"}>•</span>
              <span className={dark ? "text-slate-500" : "text-slate-500"}>{assignmentStats.invoiced} invoiced</span>
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-md transition-shadow rounded-2xl ${dark ? "bg-[#12151e] border-[#1e2231]" : ""} ${claimStats.pending > 0 ? (dark ? 'ring-2 ring-amber-500/30' : 'ring-2 ring-amber-200') : ''}`} onClick={() => navigate("/claims")} data-testid="claims-widget">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${claimStats.pending > 0 ? (dark ? 'bg-amber-500/15' : 'bg-amber-100') : (dark ? 'bg-slate-700' : 'bg-slate-100')}`}>
                <FileQuestion className={`w-5 h-5 ${claimStats.pending > 0 ? (dark ? 'text-amber-400' : 'text-amber-600') : (dark ? 'text-slate-400' : 'text-slate-500')}`} />
              </div>
              {claimStats.pending > 0 && <Badge className="bg-amber-500 text-white border-0 text-xs animate-pulse">{claimStats.pending} pending</Badge>}
            </div>
            <p className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{claimStats.pending + claimStats.under_review + claimStats.approved + claimStats.rejected}</p>
            <p className={`text-xs mb-2 ${dark ? "text-slate-500" : "text-slate-500"}`}>Claim Requests</p>
            <div className="flex items-center gap-2 text-[11px]">
              <span className={`px-1.5 py-0.5 rounded ${dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"}`}>{claimStats.pending} pending</span>
              <span className={`px-1.5 py-0.5 rounded ${dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-700"}`}>{claimStats.under_review} review</span>
              <span className={`px-1.5 py-0.5 rounded ${dark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}>{claimStats.approved} done</span>
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-md transition-shadow rounded-2xl ${dark ? "bg-[#12151e] border-[#1e2231]" : ""}`} onClick={() => navigate("/resources")} data-testid="resources-widget">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dark ? "bg-blue-500/15" : "bg-blue-100"}`}>
                <Users className={`w-5 h-5 ${dark ? "text-blue-400" : "text-blue-600"}`} />
              </div>
              <ArrowRight className={`w-4 h-4 ${dark ? "text-slate-600" : "text-slate-400"}`} />
            </div>
            <p className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{topResources?.length || 0}</p>
            <p className={`text-xs mb-2 ${dark ? "text-slate-500" : "text-slate-500"}`}>Active Resources</p>
            {topResources?.[0] && (
              <p className={`text-[11px] ${dark ? "text-slate-500" : "text-slate-500"}`}>
                Top: <span className={`font-medium ${dark ? "text-slate-300" : "text-slate-700"}`}>{topResources[0].name?.split('@')[0]}</span>
                <span className={`ml-1 ${dark ? "text-emerald-400" : "text-emerald-600"}`}>₹{formatINR(topResources[0].amount)}</span>
              </p>
            )}
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-md transition-shadow rounded-2xl ${dark ? "bg-[#12151e] border-[#1e2231]" : ""}`} onClick={() => navigate("/batches")} data-testid="batches-widget">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dark ? "bg-teal-500/15" : "bg-teal-100"}`}>
                <Receipt className={`w-5 h-5 ${dark ? "text-teal-400" : "text-teal-600"}`} />
              </div>
              <ArrowRight className={`w-4 h-4 ${dark ? "text-slate-600" : "text-slate-400"}`} />
            </div>
            <p className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{enhanced?.total_invoices || 0}</p>
            <p className={`text-xs mb-2 ${dark ? "text-slate-500" : "text-slate-500"}`}>Generated Invoices</p>
            <div className="flex items-center gap-2 text-[11px]">
              <span className={`font-medium ${dark ? "text-emerald-400" : "text-emerald-600"}`}>{enhanced?.invoice_status_summary?.paid || 0} paid</span>
              <span className={dark ? "text-slate-600" : "text-slate-400"}>•</span>
              <span className={dark ? "text-amber-400" : "text-amber-600"}>{(enhanced?.invoice_status_summary?.sent || 0) + (enhanced?.invoice_status_summary?.generated || 0)} pending</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pendencies Alert */}
      {pendencies.total_blocked > 0 && (
        <div className={`border rounded-xl p-4 mb-6 ${dark ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"}`} data-testid="pendencies-alert">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className={`text-sm font-bold ${dark ? "text-red-400" : "text-red-800"}`}>Invoice Pendencies — {pendencies.total_blocked} translator(s) blocked</h3>
          </div>
          <p className={`text-xs mb-3 ${dark ? "text-red-400/70" : "text-red-600"}`}>These translators have pending missing project claims. Their invoices cannot be auto-generated until claims are resolved.</p>
          <div className="space-y-2">
            {pendencies.pendencies.map((p, i) => (
              <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 ${dark ? "bg-[#12151e] border border-red-500/15" : "bg-white border border-red-100"}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${dark ? "bg-red-500/15" : "bg-red-100"}`}>
                    <Users className={`w-3.5 h-3.5 ${dark ? "text-red-400" : "text-red-600"}`} />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${dark ? "text-slate-300" : "text-slate-800"}`}>{p.display_name}</p>
                    <p className="text-[10px] text-slate-400">{p.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] border-0 ${dark ? "bg-red-500/15 text-red-400" : "bg-red-100 text-red-700"}`}>{p.pending_count} claim(s)</Badge>
                  <button
                    onClick={() => navigate("/claims")}
                    className={`text-[10px] font-medium px-2 py-1 rounded ${dark ? "text-red-400 bg-red-500/15 hover:bg-red-500/25" : "text-red-700 bg-red-100 hover:bg-red-200"}`}
                  >
                    Resolve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress + Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* Completion Progress */}
        <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className={`text-sm font-medium ${dark ? "text-slate-400" : "text-slate-600"}`}>Task Completion</p>
              <span className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{completionPct}%</span>
            </div>
            <Progress value={completionPct} className="h-3 mb-3" />
            <div className="flex justify-between text-xs text-slate-500">
              <span>{stats?.completed_tasks || 0} completed</span>
              <span>{(stats?.total_tasks || 0) - (stats?.completed_tasks || 0)} remaining</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className={`flex items-center gap-2 p-2 rounded-lg ${dark ? "bg-blue-500/10" : "bg-blue-50"}`}>
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-blue-500">Verified</p>
                  <p className={`text-sm font-semibold ${dark ? "text-blue-300" : "text-blue-900"}`}>{stats?.verified_rows || 0}</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 p-2 rounded-lg ${dark ? "bg-amber-500/10" : "bg-amber-50"}`}>
                <Clock className="w-4 h-4 text-amber-500" />
                <div>
                  <p className="text-xs text-amber-500">Pending</p>
                  <p className={`text-sm font-semibold ${dark ? "text-amber-300" : "text-amber-900"}`}>{stats?.pending_rows || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Financial Summary */}
        <Card className={`border-0 shadow-sm rounded-2xl lg:col-span-2 ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
          <CardContent className="p-6">
            <p className={`text-sm font-medium mb-4 ${dark ? "text-slate-400" : "text-slate-600"}`}>Invoice Financial Summary</p>
            <div className="grid grid-cols-3 gap-4">
              <div className={`p-4 rounded-xl ${dark ? "bg-slate-500/10 border border-slate-700/50" : "bg-slate-50 border border-slate-100"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className={`w-4 h-4 ${dark ? "text-slate-400" : "text-slate-500"}`} />
                  <p className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>Total Invoiced</p>
                </div>
                <p className={`text-xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`} data-testid="total-invoiced">
                  ₹{(enhanced?.total_invoiced || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-slate-400 mt-1">{enhanced?.total_invoices || 0} invoices</p>
              </div>
              <div className={`p-4 rounded-xl ${dark ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-100"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className={`w-4 h-4 ${dark ? "text-emerald-400" : "text-emerald-600"}`} />
                  <p className={`text-xs ${dark ? "text-emerald-400" : "text-emerald-600"}`}>Paid</p>
                </div>
                <p className={`text-xl font-bold ${dark ? "text-emerald-300" : "text-emerald-700"}`} data-testid="total-paid">
                  ₹{(enhanced?.total_paid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
                <p className={`text-xs mt-1 ${dark ? "text-emerald-400/70" : "text-emerald-500"}`}>{enhanced?.invoice_status_summary?.paid || 0} invoices</p>
              </div>
              <div className={`p-4 rounded-xl ${dark ? "bg-amber-500/10 border border-amber-500/20" : "bg-amber-50 border border-amber-100"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className={`w-4 h-4 ${dark ? "text-amber-400" : "text-amber-600"}`} />
                  <p className={`text-xs ${dark ? "text-amber-400" : "text-amber-600"}`}>Outstanding</p>
                </div>
                <p className={`text-xl font-bold ${dark ? "text-amber-300" : "text-amber-700"}`} data-testid="total-outstanding">
                  ₹{(enhanced?.total_outstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
                <p className={`text-xs mt-1 ${dark ? "text-amber-400/70" : "text-amber-500"}`}>
                  {(enhanced?.invoice_status_summary?.generated || 0) + (enhanced?.invoice_status_summary?.sent || 0) + (enhanced?.invoice_status_summary?.unpaid || 0)} pending
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {/* Task Distribution Pie */}
        <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
          <CardHeader className="pb-4">
            <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
              <Layers className="w-5 h-5 text-blue-600" />
              Task Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="">
            {taskTypeDist?.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie
                      data={taskTypeDist}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      innerRadius={45}
                      paddingAngle={2}
                      strokeWidth={0}
                      cursor="pointer"
                      onClick={(data) => drillTo("/adjustments", { task_type: data.name })}
                    >
                      {taskTypeDist.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip dark={dark} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-1/2 space-y-2 pl-4">
                  {taskTypeDist.map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 cursor-pointer rounded px-1 -mx-1 transition-colors ${dark ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}`} onClick={() => drillTo("/adjustments", { task_type: item.name })}>
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className={`text-xs truncate ${dark ? "text-slate-400" : "text-slate-600"}`}>{item.name}</span>
                      <span className={`text-xs font-semibold ml-auto ${dark ? "text-slate-200" : "text-slate-900"}`}>{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className={`text-sm text-center py-8 ${dark ? "text-slate-500" : "text-slate-500"}`}>No task data available</p>
            )}
          </CardContent>
        </Card>

        {/* Top Resources Bar Chart */}
        <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
          <CardHeader className="pb-4">
            <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
              <Users className="w-5 h-5 text-emerald-600" />
              Top Resources by Amount
            </CardTitle>
          </CardHeader>
          <CardContent className="">
            {topResources?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={topResources.map(r => ({
                    ...r,
                    name: r.name?.split('@')[0] || r.name,
                  }))}
                  layout="vertical"
                  margin={{ left: 0, right: 10, top: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e2231" : "#f1f5f9"} horizontal={false} />
                  <XAxis type="number" tickFormatter={formatINR} tick={{ fontSize: 11, fill: dark ? '#64748b' : '#94a3b8' }} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: dark ? '#94a3b8' : '#475569' }} />
                  <Tooltip content={<CustomTooltip dark={dark} />} />
                  <Bar dataKey="amount" name="Amount" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={18} cursor="pointer" onClick={(data) => drillTo("/resource-center", { search: data.name })} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className={`text-sm text-center py-8 ${dark ? "text-slate-500" : "text-slate-500"}`}>No resource data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Analytics Row: Monthly Trends + Rate Coverage */}
      {analytics && (
        <>
          {/* Monthly Trends Area Chart */}
          <Card className={`card mb-6 ${dark ? "bg-[#12151e] border-[#1e2231]" : ""}`} data-testid="monthly-trends-chart">
            <CardHeader className="pb-4">
              <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
                <Calendar className="w-5 h-5 text-indigo-600" />
                {trendGranularity === "daily" ? "Daily" : trendGranularity === "weekly" ? "This Week" : trendGranularity === "monthly" ? "This Month" : ""} Task & Revenue Trends
              </CardTitle>
            </CardHeader>
            <CardContent className="">
              {trendData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="gradTasks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradAmount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e2231" : "#f1f5f9"} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: dark ? "#64748b" : "#94a3b8" }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: dark ? "#64748b" : "#94a3b8" }} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={formatINR} tick={{ fontSize: 11, fill: dark ? "#64748b" : "#94a3b8" }} />
                    <Tooltip content={<CustomTooltip dark={dark} />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: dark ? "#94a3b8" : undefined }} />
                    <Area yAxisId="left" type="monotone" dataKey="tasks" name="Tasks" stroke="#6366f1" fill="url(#gradTasks)" strokeWidth={2} />
                    <Area yAxisId="right" type="monotone" dataKey="amount" name="Amount (₹)" stroke="#10b981" fill="url(#gradAmount)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className={`text-sm text-center py-8 ${dark ? "text-slate-500" : "text-slate-500"}`}>No date data available</p>
              )}
            </CardContent>
          </Card>

          {/* Language + Top Projects + Rate Coverage Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {/* Language Distribution */}
            <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`} data-testid="language-breakdown">
              <CardHeader className="pb-4">
                <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
                  <Globe className="w-5 h-5 text-cyan-600" />
                  Language Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="">
                {langBreakdown?.length > 0 ? (
                  <div className="space-y-2.5">
                    {langBreakdown.slice(0, 8).map((lang, i) => {
                      const totalTasks = langBreakdown.reduce((s, l) => s + l.count, 0);
                      const pct = totalTasks > 0 ? (lang.count / totalTasks * 100) : 0;
                      return (
                        <div key={lang.language} className={`flex items-center gap-2 cursor-pointer rounded px-1 -mx-1 transition-colors ${dark ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}`} onClick={() => drillTo("/adjustments", { language: lang.language })}>
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className={`text-xs flex-1 truncate ${dark ? "text-slate-400" : "text-slate-600"}`}>{lang.language}</span>
                          <span className={`text-xs font-mono w-8 text-right ${dark ? "text-slate-500" : "text-slate-400"}`}>{lang.count}</span>
                          <div className={`w-20 rounded-full h-1.5 ${dark ? "bg-slate-700" : "bg-slate-100"}`}>
                            <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          </div>
                          <span className={`text-[10px] w-10 text-right ${dark ? "text-slate-500" : "text-slate-400"}`}>{pct.toFixed(0)}%</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm text-center py-8 ${dark ? "text-slate-500" : "text-slate-500"}`}>No language data</p>
                )}
              </CardContent>
            </Card>

            {/* Top Projects */}
            <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`} data-testid="top-projects-chart">
              <CardHeader className="pb-4">
                <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
                  <FolderOpen className="w-5 h-5 text-amber-600" />
                  Top Projects by Revenue
                </CardTitle>
              </CardHeader>
              <CardContent className="">
                {topProjects?.length > 0 ? (
                  <div className="space-y-2.5">
                    {topProjects.slice(0, 7).map((proj, i) => {
                      const maxAmount = topProjects[0]?.amount || 1;
                      const pct = (proj.amount / maxAmount) * 100;
                      return (
                        <div key={proj.project} className="group cursor-pointer" onClick={() => drillTo("/adjustments", { search: proj.project })}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className={`text-xs font-medium truncate max-w-[140px] ${dark ? "text-slate-300" : "text-slate-700"}`}>{proj.project}</span>
                            <span className={`text-xs font-mono font-semibold ${dark ? "text-slate-200" : "text-slate-900"}`}>₹{formatINR(proj.amount)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`flex-1 rounded-full h-2 ${dark ? "bg-slate-700" : "bg-slate-100"}`}>
                              <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                            </div>
                            <span className={`text-[9px] w-16 text-right ${dark ? "text-slate-500" : "text-slate-400"}`}>{proj.count} tasks</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm text-center py-8 ${dark ? "text-slate-500" : "text-slate-500"}`}>No project data</p>
                )}
              </CardContent>
            </Card>

            {/* Rate Coverage */}
            <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`} data-testid="rate-coverage">
              <CardHeader className="pb-4">
                <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
                  <Target className="w-5 h-5 text-emerald-600" />
                  Rate Coverage
                </CardTitle>
              </CardHeader>
              <CardContent className="">
                {rateCoverage && (
                  <div className="flex flex-col items-center">
                    <div className="relative w-32 h-32 mb-4">
                      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke={dark ? "#1e2231" : "#f1f5f9"} strokeWidth="3" />
                        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none" stroke="#10b981" strokeWidth="3"
                          strokeDasharray={`${rateCoverage.pct}, 100`}
                          strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{rateCoverage.pct}%</span>
                        <span className="text-[9px] text-slate-400">coverage</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 w-full text-center">
                      <div className={`p-2 rounded-lg ${dark ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-100"}`}>
                        <p className={`text-lg font-bold ${dark ? "text-emerald-300" : "text-emerald-700"}`}>{rateCoverage.with_rate.toLocaleString()}</p>
                        <p className={`text-[10px] ${dark ? "text-emerald-400" : "text-emerald-600"}`}>With Rate</p>
                      </div>
                      <div className={`p-2 rounded-lg ${dark ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-100"}`}>
                        <p className={`text-lg font-bold ${dark ? "text-red-400" : "text-red-600"}`}>{rateCoverage.without_rate.toLocaleString()}</p>
                        <p className={`text-[10px] ${dark ? "text-red-400" : "text-red-500"}`}>Missing Rate</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Bottom Row: Invoice Status + Recent Invoices + Exceptions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* Invoice Status Breakdown */}
        <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
          <CardHeader className="pb-4">
            <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
              <CreditCard className="w-5 h-5 text-violet-600" />
              Invoice Status
            </CardTitle>
          </CardHeader>
          <CardContent className="">
            {invoiceStatusData.length > 0 ? (
              <div className="space-y-3">
                {Object.entries(enhanced?.invoice_status_summary || {}).map(([key, value]) => {
                  const colors = STATUS_COLORS[key] || STATUS_COLORS.generated;
                  const pct = enhanced?.total_invoices
                    ? Math.round((value / enhanced.total_invoices) * 100)
                    : 0;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                      <span className={`text-sm capitalize flex-1 ${dark ? "text-slate-400" : "text-slate-600"}`}>{key}</span>
                      <span className={`text-sm font-semibold ${dark ? "text-slate-200" : "text-slate-900"}`}>{value}</span>
                      <span className={`text-xs w-10 text-right ${dark ? "text-slate-500" : "text-slate-400"}`}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={`text-sm text-center py-4 ${dark ? "text-slate-500" : "text-slate-500"}`}>No invoices yet</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className={`border-0 shadow-sm rounded-2xl lg:col-span-2 ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
          <CardHeader className="pb-4">
            <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
              <FileText className={`w-5 h-5 ${dark ? "text-slate-400" : "text-slate-500"}`} />
              Recent Invoices
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {enhanced?.recent_invoices?.length > 0 ? (
              <div className={`divide-y ${dark ? "divide-slate-700/50" : "divide-slate-100"}`}>
                {enhanced.recent_invoices.map((inv) => {
                  const colors = STATUS_COLORS[inv.status] || STATUS_COLORS.generated;
                  return (
                    <div key={inv.id} className={`flex items-center justify-between px-6 py-3 transition-colors ${dark ? "hover:bg-slate-700/20" : "hover:bg-slate-50"}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${dark ? "bg-slate-700/50" : "bg-slate-100"}`}>
                          <FileText className={`w-4 h-4 ${dark ? "text-slate-400" : "text-slate-500"}`} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${dark ? "text-slate-200" : "text-slate-900"}`}>{inv.resource_name}</p>
                          <p className={`text-xs font-mono ${dark ? "text-slate-500" : "text-slate-400"}`}>{inv.invoice_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <Badge className={`${dark ? `${colors.bg.replace('bg-', 'bg-').replace('-100', '-500/15')} ${colors.text.replace('-700', '-400')}` : `${colors.bg} ${colors.text}`} text-xs capitalize border-0`}>
                          {inv.status}
                        </Badge>
                        <p className={`text-sm font-semibold w-28 text-right ${dark ? "text-slate-200" : "text-slate-900"}`}>
                          ₹{(inv.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={`text-center py-8 ${dark ? "text-slate-500" : "text-slate-500"}`}>
                <FileText className={`w-10 h-10 mx-auto mb-2 ${dark ? "text-slate-600" : "text-slate-300"}`} />
                <p className="text-sm">No invoices generated yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exception Summary */}
      <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
        <CardHeader className="pb-4">
          <CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}>
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Exception Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${dark ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-100"}`}>
              <p className={`text-2xl font-bold ${dark ? "text-red-400" : "text-red-700"}`}>{stats?.missing_duration || 0}</p>
              <p className={`text-xs ${dark ? "text-red-400/70" : "text-red-600"}`}>Missing Duration</p>
            </div>
            <div className={`p-4 rounded-lg ${dark ? "bg-amber-500/10 border border-amber-500/20" : "bg-amber-50 border border-amber-100"}`}>
              <p className={`text-2xl font-bold ${dark ? "text-amber-400" : "text-amber-700"}`}>{stats?.missing_rate || 0}</p>
              <p className={`text-xs ${dark ? "text-amber-400/70" : "text-amber-600"}`}>Missing Rate</p>
            </div>
            <div className={`p-4 rounded-lg ${dark ? "bg-orange-500/10 border border-orange-500/20" : "bg-orange-50 border border-orange-100"}`}>
              <p className={`text-2xl font-bold ${dark ? "text-orange-400" : "text-orange-700"}`}>{stats?.duplicates || 0}</p>
              <p className={`text-xs ${dark ? "text-orange-400/70" : "text-orange-600"}`}>Duplicates</p>
            </div>
            <div className={`p-4 rounded-lg ${dark ? "bg-slate-500/10 border border-slate-700/50" : "bg-slate-50 border border-slate-200"}`}>
              <p className={`text-2xl font-bold ${dark ? "text-slate-300" : "text-slate-700"}`}>{stats?.not_found || 0}</p>
              <p className={`text-xs ${dark ? "text-slate-400" : "text-slate-600"}`}>Not Found</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ══ ADVANCED ANALYTICS (from filtered data) ══ */}
      {filtered && (
        <>
          {/* Row: Daily Volume + Status Breakdown + Batch Coverage */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {/* Daily Task Volume */}
            <Card className={`border-0 shadow-sm rounded-2xl lg:col-span-2 ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
              <CardHeader className="pb-4"><CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}><TrendingUp className="w-5 h-5 text-blue-600" />Daily Task Volume</CardTitle></CardHeader>
              <CardContent className="">
                {filtered.daily_volume?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={filtered.daily_volume.map(d => ({ date: d.date.slice(5), count: d.count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e2231" : "#f1f5f9"} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: dark ? "#64748b" : "#94a3b8" }} />
                      <YAxis tick={{ fontSize: 10, fill: dark ? "#64748b" : "#94a3b8" }} />
                      <Tooltip content={<CustomTooltip dark={dark} />} />
                      <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#3b82f6" fillOpacity={dark ? 0.2 : 0.15} strokeWidth={2} name="Tasks" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <div className={`h-[200px] flex items-center justify-center text-sm ${dark ? "text-slate-500" : "text-slate-400"}`}>No daily data</div>}
              </CardContent>
            </Card>

            {/* Batch Coverage */}
            <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
              <CardHeader className="pb-4"><CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}><Target className="w-5 h-5 text-emerald-600" />Batch Coverage</CardTitle></CardHeader>
              <CardContent className="">
                {filtered.batch_coverage && (
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-28 h-28">
                      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={dark ? "#1e2231" : "#f1f5f9"} strokeWidth="3" />
                        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#6366f1" strokeWidth="3" strokeDasharray={`${filtered.batch_coverage.pct}, 100`} strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-900"}`}>{filtered.batch_coverage.pct}%</span>
                        <span className="text-[9px] text-slate-400">batched</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 w-full text-center">
                      <div className={`p-2 rounded-lg ${dark ? "bg-indigo-500/10 border border-indigo-500/20" : "bg-indigo-50 border border-indigo-100"}`}>
                        <p className={`text-lg font-bold ${dark ? "text-indigo-300" : "text-indigo-700"}`}>{filtered.batch_coverage.batched.toLocaleString()}</p>
                        <p className={`text-[10px] ${dark ? "text-indigo-400" : "text-indigo-600"}`}>In Batch</p>
                      </div>
                      <div className={`p-2 rounded-lg ${dark ? "bg-slate-500/10 border border-slate-700/50" : "bg-slate-50 border border-slate-200"}`}>
                        <p className={`text-lg font-bold ${dark ? "text-slate-300" : "text-slate-600"}`}>{filtered.batch_coverage.unbatched.toLocaleString()}</p>
                        <p className={`text-[10px] ${dark ? "text-slate-400" : "text-slate-500"}`}>Unbatched</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row: Cost Per Unit + Resource Efficiency */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            {/* Cost Per Unit by Task Type */}
            <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
              <CardHeader className="pb-4"><CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}><IndianRupee className="w-5 h-5 text-teal-600" />Cost Per Unit by Task Type</CardTitle></CardHeader>
              <CardContent className="">
                {filtered.cost_per_unit?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={filtered.cost_per_unit.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e2231" : "#f1f5f9"} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: dark ? "#64748b" : "#94a3b8" }} tickFormatter={v => `₹${v}`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: dark ? "#94a3b8" : "#94a3b8" }} width={80} />
                      <Tooltip content={<CustomTooltip dark={dark} />} />
                      <Bar dataKey="cost_per_unit" fill="#14b8a6" radius={[0, 4, 4, 0]} name="Cost/Unit" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className={`h-[240px] flex items-center justify-center text-sm ${dark ? "text-slate-500" : "text-slate-400"}`}>No data</div>}
              </CardContent>
            </Card>

            {/* Resource Efficiency */}
            <Card className={`border-0 shadow-sm rounded-2xl ${dark ? "bg-[#12151e] border border-[#1e2231]" : ""}`}>
              <CardHeader className="pb-4"><CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}><Users className="w-5 h-5 text-violet-600" />Resource Efficiency (Avg ₹/Task)</CardTitle></CardHeader>
              <CardContent className="">
                {filtered.resource_efficiency?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={filtered.resource_efficiency.slice(0, 10).map(r => ({ name: r.name.split("@")[0].slice(0, 12), avg: r.avg_per_task, tasks: r.tasks }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1e2231" : "#f1f5f9"} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: dark ? "#64748b" : "#94a3b8" }} tickFormatter={v => `₹${v}`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: dark ? "#94a3b8" : "#94a3b8" }} width={85} />
                      <Tooltip content={<CustomTooltip dark={dark} />} />
                      <Bar dataKey="avg" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Avg/Task" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className={`h-[240px] flex items-center justify-center text-sm ${dark ? "text-slate-500" : "text-slate-400"}`}>No data</div>}
              </CardContent>
            </Card>
          </div>

          {/* Row: Language x Task Type Matrix */}
          {filtered.lang_type_matrix?.length > 0 && (
            <Card className={`card mb-6 ${dark ? "bg-[#12151e] border-[#1e2231]" : ""}`}>
              <CardHeader className="pb-4"><CardTitle className={`text-base font-semibold flex items-center gap-2 ${dark ? "text-slate-200" : ""}`}><Globe className="w-5 h-5 text-cyan-600" />Language x Task Type Matrix</CardTitle></CardHeader>
              <CardContent className="">
                <div className="overflow-x-auto">
                  {(() => {
                    const langs = [...new Set(filtered.lang_type_matrix.map(m => m.language))].sort();
                    const types = [...new Set(filtered.lang_type_matrix.map(m => m.task_type))].sort();
                    const lookup = {};
                    filtered.lang_type_matrix.forEach(m => { lookup[`${m.language}|${m.task_type}`] = m; });
                    const maxCount = Math.max(...filtered.lang_type_matrix.map(m => m.count), 1);
                    return (
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr>
                            <th className={`px-2 py-1.5 text-left text-[10px] font-semibold sticky left-0 ${dark ? "text-slate-400 bg-[#12151e]" : "text-slate-500 bg-white"}`}>Language</th>
                            {types.map(t => <th key={t} className={`px-2 py-1.5 text-center text-[10px] font-semibold ${dark ? "text-slate-400" : "text-slate-500"}`}>{t}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {langs.map(lg => (
                            <tr key={lg} className={`border-t ${dark ? "border-slate-700/50" : "border-slate-100"}`}>
                              <td className={`px-2 py-1.5 font-medium sticky left-0 ${dark ? "text-slate-300 bg-[#12151e]" : "text-slate-700 bg-white"}`}>{lg}</td>
                              {types.map(tt => {
                                const cell = lookup[`${lg}|${tt}`];
                                const opacity = cell ? Math.max(0.1, cell.count / maxCount) : 0;
                                return (
                                  <td key={tt} className="px-2 py-1.5 text-center">
                                    {cell ? (
                                      <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `rgba(99, 102, 241, ${opacity})`, color: opacity > 0.5 ? "white" : (dark ? "#a5b4fc" : "#4338ca") }}>
                                        {cell.count}
                                      </span>
                                    ) : <span className={dark ? "text-slate-600" : "text-slate-200"}>-</span>}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
  </div>
  );
};

export default Dashboard;
